import clsx from "clsx";
import { ChevronDown, Pencil, Plus, Search, UserCheck, UserX } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { WholesaleCustomerView } from "../../../shared/contracts.js";
import { flattenPages } from "../../api/caseQueries.js";
import { mutationErrorMessage } from "../../api/caseMutations.js";
import { useCustomerList, useSetCustomerActive } from "../../api/wholesaleQueries.js";
import { AsyncPanel } from "../../ui/AsyncPanel.js";
import { Button } from "../../ui/Button.js";
import { ConfirmDialog } from "../../ui/Dialog.js";
import { SEARCH_DEBOUNCE_MS } from "../cases/workspace/CaseListPane.js";
import { formatInstant } from "../cases/detail/ActivityTimeline.js";
import { CustomerDialog } from "./CustomerDialog.js";
import { customerOrdersPath } from "./presentation.js";
import { WholesaleShell } from "./WholesaleShell.js";

/**
 * `/wholesale/customers` — the customer directory (spec §4.6): the
 * dealership's wholesale customers, for Parts to search, add, edit,
 * deactivate and reactivate, with each customer's order history one
 * click away (the All orders list narrowed to them).
 *
 * The list reads `GET /api/wholesale/customers` — active customers by
 * name, a search after a short pause, inactive ones only when "Show
 * inactive" is on. Add and Edit share `CustomerDialog`; Deactivate asks
 * once and posts the customer's version; Reactivate is one click. A
 * deactivated customer stays on its old orders and cannot be picked for
 * new ones; nothing is ever deleted. Parts and Parts manager only — the
 * shell sends every other role home.
 */

export function CustomersPage() {
  return (
    <WholesaleShell page activeDestination="customers">
      <CustomersContent />
    </WholesaleShell>
  );
}

function CustomersContent() {
  const titleId = useId();
  const searchId = useId();
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<WholesaleCustomerView | null>(null);
  const [deactivating, setDeactivating] = useState<WholesaleCustomerView | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const list = useCustomerList({ q: query, includeInactive });
  const setActive = useSetCustomerActive();
  const rows = flattenPages(list.data);
  const loaded = list.data !== undefined;

  const onSearch = (next: string) => {
    setDraft(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setQuery(next.trim()), SEARCH_DEBOUNCE_MS);
  };

  const toggleActive = (customer: WholesaleCustomerView, active: boolean) => {
    setActionError(null);
    setActive.mutate(
      { customerId: customer.id, active, expectedVersion: customer.version },
      {
        onSuccess: () => setAnnouncement(`${customer.name} ${active ? "reactivated" : "deactivated"}.`),
        onError: (error) => setActionError(mutationErrorMessage(error)),
      },
    );
  };

  return (
    <div className="order-page customers-page">
      <header className="order-page__header customers-page__header">
        <div>
          <h1 id={titleId} className="order-page__title">
            Customers
          </h1>
          <p className="order-page__lead">
            Body shops, independents and other dealers the counter sells to. Add a customer once, pick them on every
            order. A deactivated customer stays on its old orders and cannot be picked for new ones.
          </p>
        </div>
        <Button variant="primary" icon={<Plus />} onClick={() => setAdding(true)}>
          Add customer
        </Button>
      </header>

      <p role="status" aria-live="polite" className={clsx("inspector__announcement", !announcement && "inspector__announcement--empty")}>
        {announcement}
      </p>

      <div className="list-pane__controls customers-page__controls">
        <div className="list-pane__search" role="search">
          <Search className="list-pane__search-icon" aria-hidden="true" focusable="false" />
          <input
            id={searchId}
            type="search"
            className="text-input list-pane__search-input"
            data-size="desktop"
            aria-label="Search customers"
            placeholder="Business name…"
            autoComplete="off"
            spellCheck={false}
            value={draft}
            onChange={(event) => onSearch(event.target.value)}
          />
        </div>
        <label className={clsx("filter-toggle", includeInactive && "filter-toggle--on")}>
          <input
            type="checkbox"
            className="filter-toggle__input"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
          <span className="filter-toggle__label">Show inactive</span>
        </label>
      </div>

      {actionError ? (
        <p role="alert" className="form-alert">
          {actionError}
        </p>
      ) : null}

      <AsyncPanel
        isLoading={list.isPending}
        error={loaded ? undefined : list.error}
        refreshError={loaded ? list.error : undefined}
        refreshTitle="Couldn't refresh the customers."
        onRetry={() => {
          void list.refetch();
        }}
        loadingLabel="Loading customers…"
        isEmpty={rows.length === 0}
        emptyTitle={query ? "No customers match" : "No customers yet"}
        emptyDescription={query ? "Try another name, or show inactive customers." : "Add the first wholesale customer to start taking orders."}
      >
        <table className="report__table customers-table" aria-label="Customers">
          <thead>
            <tr>
              <th scope="col">Business</th>
              <th scope="col">Contact</th>
              <th scope="col">Phone</th>
              <th scope="col">Email</th>
              <th scope="col">Updated</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((customer) => (
              <tr key={customer.id} className="customers-table__row" data-inactive={customer.active ? undefined : "true"}>
                <td>
                  <span className="customers-table__name">{customer.name}</span>
                  {!customer.active ? <span className="customers-table__inactive">Inactive</span> : null}
                  {customer.notes ? <span className="customers-table__notes">{customer.notes}</span> : null}
                </td>
                <td>{customer.contactName ?? "—"}</td>
                <td>{customer.phone ?? "—"}</td>
                <td>{customer.email ?? <span className="inspector__muted">No email</span>}</td>
                <td>
                  <time dateTime={customer.updatedAt}>{formatInstant(customer.updatedAt)}</time>
                </td>
                <td className="customers-table__actions">
                  <Link to={customerOrdersPath(customer.id)} className="customers-table__orders">
                    Orders
                    <span className="visually-hidden"> for {customer.name}</span>
                  </Link>
                  <Button variant="quiet" icon={<Pencil />} onClick={() => setEditing(customer)}>
                    Edit
                    <span className="visually-hidden"> {customer.name}</span>
                  </Button>
                  {customer.active ? (
                    <Button variant="quiet" icon={<UserX />} disabled={setActive.isPending} onClick={() => setDeactivating(customer)}>
                      Deactivate
                      <span className="visually-hidden"> {customer.name}</span>
                    </Button>
                  ) : (
                    <Button
                      variant="quiet"
                      icon={<UserCheck />}
                      disabled={setActive.isPending}
                      onClick={() => toggleActive(customer, true)}
                    >
                      Reactivate
                      <span className="visually-hidden"> {customer.name}</span>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.hasNextPage ? (
          <div className="list-pane__more">
            <Button
              variant="secondary"
              icon={<ChevronDown />}
              busy={list.isFetchingNextPage}
              onClick={() => {
                void list.fetchNextPage();
              }}
            >
              Show more
            </Button>
          </div>
        ) : null}
      </AsyncPanel>

      <CustomerDialog
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={(customer) => setAnnouncement(`${customer.name} added.`)}
      />
      <CustomerDialog
        open={editing !== null}
        customer={editing}
        onClose={() => setEditing(null)}
        onSaved={(customer) => setAnnouncement(`${customer.name} saved.`)}
      />
      <ConfirmDialog
        open={deactivating !== null}
        onClose={() => setDeactivating(null)}
        onConfirm={() => {
          if (deactivating) toggleActive(deactivating, false);
          setDeactivating(null);
        }}
        title={`Deactivate ${deactivating?.name ?? "this customer"}?`}
        description="They stay on their existing orders and can be reactivated later, but cannot be picked for a new order."
        confirmLabel="Deactivate"
        cancelLabel="Keep active"
        tone="destructive"
      />
    </div>
  );
}
